// @flow
import type { Reducer } from 'redux';
import { createReducer } from 'redux-act';
import { actions as formActions, SubmissionError } from 'gandi.react-form';

import {
  createResource,
  type ActionReturn,
  type FetchResponse,
  type Resource,
} from '@gandi/redux-resource';
import type { TranslatorProps } from '@gandi/translate';

import type {
  Dispatch,
  FullState,
  ResourceThunkAction,
  UnsubscriptionState,
} from '../../types';

export const resource: Resource<> = createResource('unsubscription');

type FetchUnsubscriptionInfosAction<R> = (token: string) => R;
type DeleteNotificationSubscribersAction<R> = (
  token: string,
  subscriptionKey: string
) => R;

export const fetchUnsubscriptionInfos: FetchUnsubscriptionInfosAction<
  ResourceThunkAction
> =
  (token): ((dispatch: Dispatch) => ActionReturn) =>
  dispatch =>
    dispatch(resource.fetchOne(`/organization-anonymous/subscribers/${token}`));

export const deleteNotificationSubcribers: DeleteNotificationSubscribersAction<
  ResourceThunkAction
> =
  (token, subscriptionKey): ((dispatch: Dispatch) => ActionReturn) =>
  dispatch => {
    let url = `/organization-anonymous/subscribers/${token}`;
    if (subscriptionKey !== 'all') {
      url = `/organization-anonymous/subscribers/${token}/${subscriptionKey}`;
    }
    return dispatch(resource.deleteOne(url));
  };

// Server-side POST
export function submitUnsubscriptionForm(
  values: {| unsubscription_choice: string |},
  routeParams: {| token: string |},
  __: TranslatorProps['__'],
  req: express$Request,
  res: express$Response,
  currentUrl: string
): (dispatch: Dispatch) => ActionReturn {
  const subscriptionKey = values.unsubscription_choice;
  const { token } = routeParams;
  const successUrl = `${currentUrl}/success`;

  return (dispatch: Dispatch) =>
    dispatch(deleteNotificationSubcribers(token, subscriptionKey))
      .then(() => {
        res.redirect(successUrl);
        dispatch(resource.deleteOne.success.getType());
        return Promise.reject(new Error(`Redirect to ${successUrl}`));
      })
      .catch(response => {
        const message =
          response.response.data.message ||
          __('A temporary error occured while sending your confirmation.');
        dispatch(formActions.stopSubmit('UnsubscriptionForm', message));
        throw new SubmissionError({
          unsubscription_choice: message,
        });
      });
}

// ********** SELECTORS **********
export const selectInfosUnsubscription = (
  state: FullState
): UnsubscriptionState => state.unsubscription;

// ********** HANDLERS **********
export const fetchOneHandler = (
  state: UnsubscriptionState,
  { response }: {| response: FetchResponse<200, UnsubscriptionState> |}
): UnsubscriptionState => ({
  ...response.data,
});

// ********** REDUCER **********
export const initialState = {};

const reducer: Reducer<UnsubscriptionState, ResourceThunkAction> =
  // $FlowFixMe[incompatible-exact]
  createReducer(
    {
      [resource.fetchOne.success.getType()]: fetchOneHandler,
    },
    initialState
  );

export default reducer;
